import React, { useEffect, useState } from 'react';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '@/services/api';

interface Member { bookingId: string | null; name: string; isHost: boolean; isMe: boolean }
interface OrderLine { id: string; memberName: string; status: string; total: number; items: Array<{ name: string; quantity: number; unitPrice: number | null }> }
interface Tab { id: string; code: string; tableLabel: string | null; members: Member[]; orders: OrderLine[]; total: number }

interface Props {
  bookingId: string;
  currency?: string;
  /** Called when membership changes, so the parent can refresh (orders now pool differently). */
  onChange?: () => void;
}

/**
 * Group Tab / Shared Cart bar for the order sheet. Private by default: shows a subtle "Order together?"
 * prompt. A guest can START a tab (gets a join code to share) or JOIN with a code; once in, everyone's
 * orders pool into one running tab (shown here) to split at the end. Leaving returns you to private.
 */
const GroupTabBar: React.FC<Props> = ({ bookingId, currency, onChange }) => {
  const [tab, setTab] = useState<Tab | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [joining, setJoining] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [split, setSplit] = useState<{ groupTotal: number; memberCount: number; evenShare: number; myOrdersTotal: number; paid: number; remaining: number; settled: boolean } | null>(null);
  const [splitMode, setSplitMode] = useState<'even' | 'mine'>('even');
  const [method, setMethod] = useState<'cash' | 'card' | 'upi'>('cash');
  const [payingShare, setPayingShare] = useState(false);

  const money = (n: number) => `${currency ? currency + ' ' : ''}${n.toFixed(2)}`;

  const load = () => {
    if (!bookingId) return;
    apiService.getGroupTab(bookingId).then((r) => setTab((r as any)?.data || null)).catch(() => {});
    apiService.getGroupSplit(bookingId).then((r) => setSplit((r as any)?.data || null)).catch(() => {});
  };
  const myShare = split ? (splitMode === 'even' ? split.evenShare : split.myOrdersTotal) : 0;
  const payMyShare = async () => {
    if (payingShare || myShare <= 0) return;
    setPayingShare(true); setErr('');
    try { await apiService.payGroupShare(bookingId, myShare, method); load(); onChange?.(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Payment failed'); }
    finally { setPayingShare(false); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000); // see teammates' orders appear
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const start = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try { const r = await apiService.startGroupTab(bookingId); setTab((r as any)?.data || null); setOpen(true); onChange?.(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not start'); }
    finally { setBusy(false); }
  };
  const join = async () => {
    if (busy || !code.trim()) return;
    setBusy(true); setErr('');
    try { const r = await apiService.joinGroupTab(bookingId, code.trim()); setTab((r as any)?.data || null); setJoining(false); setCode(''); setOpen(true); onChange?.(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not join'); }
    finally { setBusy(false); }
  };
  const leave = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try { await apiService.leaveGroupTab(bookingId); setTab(null); setOpen(false); onChange?.(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not leave'); }
    finally { setBusy(false); }
  };

  // ---- Not in a tab: the opt-in prompt ----
  if (!tab) {
    return (
      <div style={promptWrap}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Users size={16} color="#e0531f" />
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Order together?</span>
        </span>
        {joining ? (
          <span style={{ display: 'flex', gap: 6 }}>
            <input style={codeInput} placeholder="Code" value={code} maxLength={6} inputMode="numeric" onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter') join(); }} autoFocus />
            <button style={miniBtn} disabled={busy || !code.trim()} onClick={join}>Join</button>
            <button style={miniGhost} onClick={() => { setJoining(false); setErr(''); }}>✕</button>
          </span>
        ) : (
          <span style={{ display: 'flex', gap: 6 }}>
            <button style={miniBtn} disabled={busy} onClick={start}>Start a tab</button>
            <button style={miniGhost} onClick={() => { setJoining(true); setErr(''); }}>Join with code</button>
          </span>
        )}
        {err && <div style={errText}>{err}</div>}
      </div>
    );
  }

  // ---- In a tab: banner + expandable pooled view ----
  return (
    <div style={activeWrap}>
      <button type="button" style={bannerRow} onClick={() => setOpen((o) => !o)}>
        <Users size={16} color="#fff" />
        <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
          <span style={bannerTitle}>Group tab · Code {tab.code}</span>
          <span style={bannerSub}>{tab.members.length} {tab.members.length === 1 ? 'person' : 'people'} · {money(tab.total)}</span>
        </span>
        {open ? <ChevronUp size={18} color="#fff" /> : <ChevronDown size={18} color="#fff" />}
      </button>

      {open && (
        <div style={panel}>
          <div style={hint}>Share code <b>{tab.code}</b> with your table. Your orders join this shared tab.</div>
          <div style={membersRow}>
            {tab.members.map((m, i) => (
              <span key={i} style={{ ...chip, ...(m.isMe ? chipMe : {}) }}>{m.name}{m.isHost ? ' 👑' : ''}{m.isMe ? ' (you)' : ''}</span>
            ))}
          </div>
          {tab.orders.length > 0 ? (
            <div style={ordersWrap}>
              {tab.orders.map((o) => (
                <div key={o.id} style={orderRow}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={orderWho}>{o.memberName}</span>
                    <span style={orderItems}>{o.items.map((it) => `${it.quantity}× ${it.name}`).join(', ')}</span>
                  </span>
                  <span style={orderTotal}>{money(o.total)}</span>
                </div>
              ))}
              <div style={totalRow}><span>Group total</span><span>{money(tab.total)}</span></div>
            </div>
          ) : (
            <div style={{ ...hint, marginTop: 4 }}>No orders yet — add items below; everyone's orders pool here.</div>
          )}
          {/* Split & pay */}
          {split && split.groupTotal > 0 && (
            <div style={splitWrap}>
              <div style={splitHead}>Split the bill</div>
              <div style={splitToggle}>
                <button type="button" style={{ ...splitTabBtn, ...(splitMode === 'even' ? splitTabOn : {}) }} onClick={() => setSplitMode('even')}>Even · {money(split.evenShare)}</button>
                <button type="button" style={{ ...splitTabBtn, ...(splitMode === 'mine' ? splitTabOn : {}) }} onClick={() => setSplitMode('mine')}>My orders · {money(split.myOrdersTotal)}</button>
              </div>
              <div style={progressText}>{money(split.paid)} of {money(split.groupTotal)} paid · {money(split.remaining)} left{split.settled ? ' · Settled ✓' : ''}</div>
              {!split.settled && (
                <>
                  <div style={methodRow}>
                    {(['cash', 'card', 'upi'] as const).map((m) => (
                      <button key={m} type="button" style={{ ...mChip, ...(method === m ? mChipOn : {}) }} onClick={() => setMethod(m)}>{m.toUpperCase()}</button>
                    ))}
                  </div>
                  <button type="button" style={{ ...payBtn, opacity: myShare > 0 && !payingShare ? 1 : 0.5 }} disabled={myShare <= 0 || payingShare} onClick={payMyShare}>
                    {payingShare ? 'Recording…' : `Pay my share · ${money(myShare)}`}
                  </button>
                </>
              )}
            </div>
          )}
          <button type="button" style={leaveBtn} disabled={busy} onClick={leave}>Leave group tab</button>
          {err && <div style={errText}>{err}</div>}
        </div>
      )}
    </div>
  );
};

const promptWrap: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', background: '#fff8f4', border: '1px dashed rgba(255,107,53,0.4)', borderRadius: 12, padding: '9px 12px', marginBottom: 12 };
const activeWrap: React.CSSProperties = { border: '1px solid rgba(255,107,53,0.35)', borderRadius: 14, overflow: 'hidden', marginBottom: 12 };
const bannerRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', padding: '11px 12px', cursor: 'pointer' };
const bannerTitle: React.CSSProperties = { display: 'block', fontWeight: 800, fontSize: '0.92rem' };
const bannerSub: React.CSSProperties = { display: 'block', fontSize: '0.78rem', opacity: 0.92 };
const panel: React.CSSProperties = { padding: '10px 12px', background: '#fff' };
const hint: React.CSSProperties = { fontSize: '0.8rem', color: '#64748b', marginBottom: 8 };
const membersRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 };
const chip: React.CSSProperties = { background: '#f1f5f9', color: '#475569', borderRadius: 9999, padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700 };
const chipMe: React.CSSProperties = { background: 'rgba(255,107,53,0.12)', color: '#e0531f' };
const ordersWrap: React.CSSProperties = { border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' };
const orderRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.84rem' };
const orderWho: React.CSSProperties = { display: 'block', fontWeight: 700, color: '#0f172a' };
const orderItems: React.CSSProperties = { display: 'block', color: '#64748b', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const orderTotal: React.CSSProperties = { fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' };
const totalRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', padding: '9px 10px', fontWeight: 800, color: '#e0531f', background: '#fff8f4' };
const leaveBtn: React.CSSProperties = { marginTop: 10, background: '#f3f4f6', color: '#b91c1c', border: 'none', borderRadius: 10, padding: '9px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', width: '100%' };
const codeInput: React.CSSProperties = { width: 68, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 8px', fontSize: '0.9rem', textAlign: 'center', outline: 'none', color: '#0f172a' };
const miniBtn: React.CSSProperties = { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer' };
const miniGhost: React.CSSProperties = { background: 'transparent', color: '#e0531f', border: '1px solid rgba(255,107,53,0.4)', borderRadius: 8, padding: '6px 10px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' };
const errText: React.CSSProperties = { width: '100%', color: '#b91c1c', fontSize: '0.78rem', marginTop: 6 };
const splitWrap: React.CSSProperties = { marginTop: 12, borderTop: '1px solid #eef2f7', paddingTop: 10 };
const splitHead: React.CSSProperties = { fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', marginBottom: 8 };
const splitToggle: React.CSSProperties = { display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 8 };
const splitTabBtn: React.CSSProperties = { flex: 1, border: 'none', background: 'transparent', color: '#64748b', fontWeight: 700, fontSize: '0.8rem', padding: '7px 6px', borderRadius: 8, cursor: 'pointer' };
const splitTabOn: React.CSSProperties = { background: '#fff', color: '#e0531f', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' };
const progressText: React.CSSProperties = { fontSize: '0.78rem', color: '#64748b', marginBottom: 8 };
const methodRow: React.CSSProperties = { display: 'flex', gap: 6, marginBottom: 8 };
const mChip: React.CSSProperties = { flex: 1, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer' };
const mChipOn: React.CSSProperties = { background: 'rgba(255,107,53,0.12)', color: '#e0531f', borderColor: 'rgba(255,107,53,0.4)' };
const payBtn: React.CSSProperties = { width: '100%', background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 800, cursor: 'pointer' };

export default GroupTabBar;
